/*
 * Copyright 2025 Intuition Robotics ltd
 */
import React from 'react';
import {BaseComponent} from "@intuitionrobotics/thunderstorm/frontend";
import {Frames, GraphsModule} from "../../../_to_remove/GraphsModule";
import dayjs from "../../../utils/dayjs";
import AxisY from "../../../_to_remove/AxisY";
import AxisX from "../../../_to_remove/AxisX";
import {COLORS} from "@res/colors";
import {FONTS} from "@res/fonts";
import {ScaleLinear} from "d3-scale";


export type PainData = {
	id: string;
	timestamp: number;
	bodyPart: string;
	level: number;
}

type Props = {
	data: PainData[];
	viewBox: number;
	frequency: number;
	height: number;
	width?: number;
	endDate: number;
	frames?: Frames;
}

type State = {
}

export class PainGraph
	extends BaseComponent<Props, State> {

	private readonly circleWidth = 3;
	private readonly textBoxHeight = 15;
	private readonly textBoxWidth = 80;
	private readonly colorPalette = ["#EE9C2C",
	                                 "#DF3A8B",
	                                 "#026691",
	                                 "#4F3030",
	                                 "#6C8D13",
	                                 "#53BAD5"];
	private readonly margin = {
		top: 20,
		bottom: 40,
		left: 0,
		right: 0
	};

	private firstInstancesOfBodyPartIds: string[] = [];

	private graphRef?: HTMLDivElement;

	constructor(props: Props) {
		super(props);
		this.state = {
		};
	}

	private collectBodyParts = (): string[] => {
		return Array.from(new Set(this.props.data.map(painDatum => painDatum.bodyPart)));
	};

	private getBodyPartColor = (bodyPart: string) => {
		const bodyParts = this.collectBodyParts();
		return this.colorPalette[bodyParts.indexOf(bodyPart) % this.colorPalette.length];
	};

	private getPainDataGroupedByTimestamp = (data?: PainData[]): [string, PainData[]][] => {
		return Object.entries(
			(data ? data : this.props.data).reduce((acc, painDatum) => {
				return {
					...acc,
					[painDatum.timestamp]: [...(acc[painDatum.timestamp] || []), painDatum]
				};
			}, {} as Record<PainData["timestamp"], PainData[]>))
	};

	private getPainDataGroupedByBodyPart = (data?: PainData[]): [string, PainData[]][] => {
		return Object.entries(
			(data ? data : this.props.data).reduce((acc, painDatum) => {
				return {
					...acc,
					[painDatum.bodyPart]: [...(acc[painDatum.bodyPart] || []), painDatum]
				};
			}, {} as Record<PainData["bodyPart"], PainData[]>))
	};

	private fillFirstInstancesOfBodyPartIds = () => {
		const firstInstancesOfBodyPartIds = this.getPainDataGroupedByBodyPart()
				.map(([, painData]) => {
					const firstInstancesOfBodyPartId: string[] = [];

					// first element is first instance of course
					if (painData.length > 0) {
						firstInstancesOfBodyPartId.push(painData[0].id);
					}
					for (let i = 0; i < painData.length - 1; i++) {
						const painDatumPrevious = painData[i];
						const painDatumAfter = painData[i + 1];
						// if previous instance was level 0 then the next instance is a new instance
						if (painDatumPrevious.level === 0) {
							firstInstancesOfBodyPartId.push(painDatumAfter.id);
						}
					}
					return firstInstancesOfBodyPartId;
				});
		this.firstInstancesOfBodyPartIds = firstInstancesOfBodyPartIds.flat(1);
	};

	private checkIfFirstInstance = (painData: PainData) : boolean => {
		return this.firstInstancesOfBodyPartIds.includes(painData.id);
	};

	private collectDottedLines = (): Record<string, { id1: string, x1: number, y1: number, id2: string, x2: number, y2: number }[]> => {
		return Object.fromEntries(
			this.getPainDataGroupedByBodyPart()
			      .map(([bodyPart, painData]) => {
				      const lines = [];
				      for (let i = 0; i < painData.length - 1; i++) {
					      const painDatum1 = painData[i];
					      const painDatum2 = painData[i + 1];
						  // only if previous data was not 0 draw a dotted line
					      if (painDatum1.level !== 0) {
						      lines.push({
							                 id1: painDatum1.id, x1: painDatum1.timestamp, y1: painDatum1.level,
							                 id2: painDatum2.id, x2: painDatum2.timestamp, y2: painDatum2.level
						                 });
					      }
				      }
				      return [bodyPart,
				              lines];
			      }));
	};

	private minAndMax = () => {
		return GraphsModule.extent(
			this.props.data.map(painDatum => {
				return {
					_id: painDatum.id,
					x: painDatum.timestamp,
					y: painDatum.level
				};
			}),
			this.props.frames
		);
	};

	private width = () => {
		const graphRef = this.graphRef;
		const width = this.props.width || graphRef?.clientWidth;
		if (!width)
			return 0;

		return width - this.margin.right - this.margin.left;
	};

	private height = () => this.props.height - this.margin.top - this.margin.bottom;

	private xScale = () => GraphsModule.scale(this.props.endDate - (this.props.frequency * (this.props.viewBox + 1)),
												this.props.endDate,
	                                          0,
	                                          this.width());

	private yScale = () => GraphsModule.yScale(this.minAndMax().minY, this.minAndMax().maxY + 1, this.height());

	private getAxesDates = () => {
		const axesNumbers = [];
		let x = this.props.endDate;
		for (let i = 0; i < this.props.viewBox; ++i) {
			axesNumbers.unshift(x);
			x = x - this.props.frequency;
		}
		return axesNumbers;
	};

	private getAxesDiv = (axesNumbers: number[]) => {
		const axesLabels = axesNumbers.map(timestamp => dayjs(timestamp).format("M/D"));
		return axesLabels.map((label, i) => {
			return (
				<div key={i}>
					<div style={{fontSize: 11, color: COLORS.neutral_text_dark_2, textAlign: "center"}}>{label}</div>
				</div>
			);
		});
	};

	private renderXAndY = () => {
		const range = this.props.frequency * this.props.viewBox;
		const minmax = this.minAndMax();
		const ticks = minmax.maxY + 1 - minmax.minY;
		const axisPoint = (this.props.endDate - range) + (this.props.frequency / 2);
		return <>
			<AxisY yScale={this.yScale()}
			       width={this.width()}
			       ticks={ticks}
			       placeInMiddle={true}
			       label={true}/>
			<AxisX xScale={this.xScale()}
			       frequency={this.props.frequency}
			       height={this.height()}
			       axisPoint={axisPoint}
			       tickValues={this.getAxesDiv(this.getAxesDates())}
			       minValue={this.props.endDate - range}
			       viewBox={this.props.viewBox}
			       shiftData={- this.props.frequency / 2}
				   alignMiddle={true}
			/>
		</>;
	};

	private renderColumns = (xScale: ScaleLinear<number, number, any>, yScale: ScaleLinear<number, number, any>) => {
		const painDataPerDay = this.getPainDataGroupedByTimestamp(this.props.data.filter(x => x.level !== 0 && this.checkIfFirstInstance(x)));
		return painDataPerDay.map(([, painData]) => painData.map(({id, bodyPart, timestamp, level}, index, array) => {
			const color = this.getBodyPartColor(bodyPart);
			const bodyPartCapitalized = bodyPart.charAt(0).toUpperCase() + bodyPart.slice(1);

			const x = xScale( timestamp - (this.props.frequency / 2)) - (this.circleWidth * 3 / 2) + 20;
			const y = yScale(level) - (this.circleWidth * 3 / 2);

			return (
				<svg key={`column-${id}`}>
					<foreignObject width={this.circleWidth * 3}
					               height={Math.abs(yScale(level) - yScale(this.minAndMax().minY) - (this.circleWidth * 3 / 2))}
					               x={x}
					               y={y}>
						<div style={{
							backgroundColor: color,
							opacity: 0.3,
							width: "100%",
							height: "100%",
							borderRadius: "5px 5px 0 0"
						}}/>
					</foreignObject>
					<foreignObject x={x + 20}
					               y={y + (array.length - 1) * 5 - (this.textBoxHeight * index)}
					               height={this.textBoxHeight}
					               width={this.textBoxWidth}>
						{bodyPart !== "generic" && <div style={{display: "flex", alignItems: "center", justifyContent: "flex-start", height: "100%", width: "100%"}}>
							{FONTS.reg_12(`${bodyPartCapitalized}`, {color: color, lineHeight: "15px"})}
						</div>}
					</foreignObject>
				</svg>
			);
		})).flat(1);
	};

	private renderDottedLines = (xScale: ScaleLinear<number, number, any>, yScale: ScaleLinear<number, number, any>) => {
		return Object.entries(this.collectDottedLines())
		             .flatMap(([bodyPart, lines]) => {
			             const color = this.getBodyPartColor(bodyPart);
			             return lines.map(({id1, x1, y1, id2, x2, y2}) => {
				             return (
					             <line key={`line-${id1}-${id2}`}
					                   x1={xScale(x1 - (this.props.frequency / 2)) + 20}
					                   x2={xScale(x2 - (this.props.frequency / 2)) + 20}
					                   y1={yScale(y1)}
					                   y2={yScale(y2)}
					                   stroke={color}
					                   strokeWidth={1}
					                   strokeDasharray={3}
					                   strokeDashoffset={3}/>
				             );
			             });
		             });
	};

	private renderDataPoints = (xScale: ScaleLinear<number, number, any>, yScale: ScaleLinear<number, number, any>) => {
		return this.props.data.filter(x => x.level !== 0 || !this.checkIfFirstInstance(x)).map(({id, timestamp, bodyPart, level}, index) => {
			const color = this.getBodyPartColor(bodyPart);
			const x = xScale(timestamp - (this.props.frequency / 2)) - (this.textBoxWidth / 2) + 20;
			const y = yScale(level) - this.circleWidth - this.textBoxHeight;
			return (
				<svg key={`point-${id}`}>
					<foreignObject x={x}
					               y={y}
					               height={this.textBoxHeight}
					               width={this.textBoxWidth}>
						<div style={{display: "flex", alignItems: "center", justifyContent: "center", height: "100%", width: "100%"}}>
							{FONTS.reg_12(`${level}`, {color: color, lineHeight: "15px"})}
						</div>
					</foreignObject>
					<circle r={this.circleWidth}
					        cx={xScale(timestamp - (this.props.frequency / 2)) + 20}
					        cy={yScale(level)}
							style={{fill: color, zIndex: 1}}/>
				</svg>
			);
		});
	};

	// @ts-ignore
	private renderLabels = (xScale: ScaleLinear<number, number, any>, yScale: ScaleLinear<number, number, any>) => {
		const painDataByTimestamp = this.getPainDataGroupedByTimestamp();
		const painDataByTimestampSortedByLevel = Object.fromEntries(
			painDataByTimestamp.map(([timestamp, painData]) => {
				return [timestamp,
				        painData.sort((a, b) => a.level - b.level)];
			})
		);
		const levelsByTimestamp = Object.keys(painDataByTimestampSortedByLevel).sort().reduce((acc, timestamp) => {
			const painData = painDataByTimestampSortedByLevel[timestamp];
			return {
				...acc,
				[timestamp]: painData.filter(painDatum => {
					const isFirstInstance = this.checkIfFirstInstance(painDatum);
					return !isFirstInstance || (isFirstInstance && !Object.values(acc).flat().map(v => v.bodyPart).includes(painDatum.bodyPart));
				})
			};
		}, {} as Record<number, PainData[]>);
		return Object.entries(levelsByTimestamp).flatMap(([key, levelsByTimestampEntry]) => {
			const timestamp = parseInt(key);
			const level = levelsByTimestampEntry.map(painDatum => painDatum.level).sort((a, b) => a - b).reverse()[0];
			return levelsByTimestampEntry.map((painDatum, index) => {
				const color = this.getBodyPartColor(painDatum.bodyPart);
				const bodyPart = painDatum.bodyPart.charAt(0).toUpperCase() + painDatum.bodyPart.slice(1);

				// console.log(`yScale for ${bodyPart} at level ${level} is ${yScale(level) - this.circleWidth - (this.textBoxHeight * index)}`)
				return (
					<svg key={`label-${painDatum.id}`} style={{overflow: "visible"}}>
						<foreignObject x={xScale(timestamp - (this.props.frequency / 2)) - this.circleWidth + 10}
						               y={yScale(level) - this.circleWidth - (this.textBoxHeight * index)}
						               height={this.textBoxHeight}
						               width={this.textBoxWidth}>
							<div style={{display: "flex", alignItems: "center", justifyContent: "flex-start", height: "100%", width: "100%"}}>
								{FONTS.reg_12(`${bodyPart}`, {color: color, lineHeight: "15px"})}
							</div>
						</foreignObject>
					</svg>
				);
			});
		});
	};

	render() {
		const yScale = this.yScale();
		const xScale = this.xScale();
		this.fillFirstInstancesOfBodyPartIds()
		return (
			<div ref={ref => {
				if (!ref || this.graphRef) {
					return;
				}
				this.graphRef = ref;
				this.forceUpdate();
			}}>
				<svg width={this.width() + 50} height={this.props.height} style={{overflow: "visible"}}>
					<g transform={`translate(${-10}, ${0})`} width={this.width()} style={{overflow: "visible"}}>
						{this.renderXAndY()}
						{this.renderColumns(xScale, yScale)}
						{this.renderDottedLines(xScale, yScale)}
						{this.renderDataPoints(xScale, yScale)}
						{/*{this.renderLabels(xScale, yScale)}*/}
					</g>
				</svg>
			</div>
		);
	}
}

export default PainGraph;
